import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AFFILIATE_BUCKETS } from '../../utils/affiliateRegionMap';
import { MAP_SIDE_RAIL_MOBILE_WIDTH } from './MapSideRail';

export const RIGHT_RAIL_PANEL_WIDTH = 340;
/** Gap between left rail and Map/Tours panel on mobile. */
const MOBILE_LEFT_RAIL_GAP = 4;
const MOBILE_RIGHT_EDGE_GAP = 4;
/** Distance from map top to content below Map/Tours tabs (px). */
export const RIGHT_RAIL_CONTENT_TOP = 58;

const THIN_SCROLLBAR_PROPS = Platform.OS === 'web'
  ? { className: 'vrc-venue-filters-scroll' }
  : {};

const MAP_VIEW_TYPES = [
  {
    key: 'map',
    label: 'Standard',
    icon: 'map-outline',
    description: 'Road map with venue pins or clusters. Best for routing and day-to-day planning.',
    previewColors: ['#ebe6dc', '#b8cfe8', '#f4efe6'],
  },
  {
    key: 'concentration',
    label: 'Concentration',
    icon: 'color-fill-outline',
    description: 'States and regions colored by dominant affiliate. Darker shades mean more venues in that area.',
    previewColors: ['#fd3132', '#1fb383', '#5792ea', '#fe65c0'],
  },
];

const BASEMAP_OPTIONS = [
  {
    key: 'streets',
    label: 'Streets',
    icon: 'map',
    description: 'Light Mapbox streets — clear labels and roads.',
    previewBg: '#ebe6dc',
  },
  {
    key: 'dark',
    label: 'Dark',
    icon: 'moon',
    description: 'Dark basemap for low-light or high-contrast planning.',
    previewBg: '#1a2332',
  },
  {
    key: 'satellite',
    label: 'Satellite',
    icon: 'earth',
    description: 'Satellite imagery with road and place labels overlaid.',
    previewBg: '#3d5a3a',
  },
  {
    key: '3d',
    label: '3D Buildings',
    icon: 'cube',
    description: 'Tilted map view with building extrusions when zoomed in.',
    previewBg: '#2c3e50',
  },
];

function MapPreviewSwatch({ colors, bg, isDarkMode, styles }) {
  if (colors?.length) {
    return (
      <View style={styles.previewSwatchRow}>
        {colors.map((c) => (
          <View key={c} style={[styles.previewSwatchSlice, { backgroundColor: c }]} />
        ))}
      </View>
    );
  }
  return (
    <View style={[styles.previewSwatchSolid, { backgroundColor: bg || (isDarkMode ? '#2a3344' : '#e8e4dc') }]}>
      <View style={styles.previewSwatchGrid} />
    </View>
  );
}

function MapTypeCard({
  active,
  label,
  icon,
  description,
  previewColors,
  previewBg,
  isDarkMode,
  onPress,
  styles,
}) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[styles.typeCard, active && styles.typeCardActive]}
      activeOpacity={0.85}
    >
      <MapPreviewSwatch colors={previewColors} bg={previewBg} isDarkMode={isDarkMode} styles={styles} />
      <View style={styles.typeCardBody}>
        <View style={styles.typeCardTitleRow}>
          <Ionicons
            name={icon}
            size={16}
            color={active ? '#ffffff' : (isDarkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.62)')}
          />
          <Text style={[styles.typeCardTitle, active && styles.typeCardTitleActive]}>{label}</Text>
        </View>
        <Text style={[styles.typeCardDesc, active && styles.typeCardDescActive]}>{description}</Text>
      </View>
    </TouchableOpacity>
  );
}

function MapSettingsPanel({
  isDarkMode,
  styles,
  mapViewMode,
  onMapViewModeChange,
  displayMode,
  onDisplayModeChange,
  concentrationType,
  onConcentrationTypeChange,
  showConcentrationSettings,
  onToggleConcentrationSettings,
  concentrationBucketsOn,
  onConcentrationBucketsChange,
  basemapActive,
  onBasemapChange,
  onLocate,
  onRecenter,
  hasUserLocation,
  masterTourUi = false,
}) {
  return (
    <ScrollView
      style={styles.panelScroll}
      contentContainerStyle={styles.panelScrollContent}
      nestedScrollEnabled
      showsVerticalScrollIndicator
      keyboardShouldPersistTaps="handled"
      {...THIN_SCROLLBAR_PROPS}
    >
      <Text style={styles.sectionLabel}>Map type</Text>
      <Text style={styles.sectionHint}>Streets, dark, satellite, or 3D buildings.</Text>
      <View style={styles.typeCardList}>
        {BASEMAP_OPTIONS.map((opt) => (
          <MapTypeCard
            key={opt.key}
            active={basemapActive === opt.key}
            label={opt.label}
            icon={opt.icon}
            description={opt.description}
            previewBg={opt.previewBg}
            isDarkMode={isDarkMode}
            onPress={() => onBasemapChange?.(opt.key)}
            styles={styles}
          />
        ))}
      </View>

      <Text style={[styles.sectionLabel, { marginTop: 18 }]}>Map view</Text>
      <Text style={styles.sectionHint}>Choose how venues appear on the map.</Text>
      <View style={styles.typeCardList}>
        {MAP_VIEW_TYPES.map((opt) => (
          <MapTypeCard
            key={opt.key}
            active={mapViewMode === opt.key}
            label={opt.label}
            icon={opt.icon}
            description={opt.description}
            previewColors={opt.previewColors}
            isDarkMode={isDarkMode}
            onPress={() => onMapViewModeChange?.(opt.key)}
            styles={styles}
          />
        ))}
      </View>

      {mapViewMode === 'concentration' && (
        <View style={styles.subPanel}>
          <View style={styles.subPanelHeader}>
            <Text style={styles.subPanelTitle}>Concentration settings</Text>
            <TouchableOpacity
              onPress={() => onToggleConcentrationSettings?.()}
              style={styles.subPanelToggle}
            >
              <Text style={styles.subPanelToggleText}>
                {showConcentrationSettings ? 'Hide' : 'Show'}
              </Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.sectionHint}>
            Zoom out for states, in for metro areas, counties, then ZIP codes.
          </Text>
          {showConcentrationSettings && (
            <>
              <View style={styles.chipRow}>
                {[{ key: 'affiliate', label: 'Affiliate' }].map((t) => {
                  const active = concentrationType === t.key;
                  return (
                    <TouchableOpacity
                      key={t.key}
                      onPress={() => {
                        if (active) onToggleConcentrationSettings?.();
                        else onConcentrationTypeChange?.(t.key);
                      }}
                      style={[styles.chip, active && styles.chipActive]}
                    >
                      <Text style={[styles.chipText, active && styles.chipTextActive]}>{t.label}</Text>
                    </TouchableOpacity>
                  );
                })}
                <TouchableOpacity
                  style={styles.chip}
                  onPress={() => onConcentrationBucketsChange?.(
                    Object.fromEntries(AFFILIATE_BUCKETS.map((b) => [b.id, true]))
                  )}
                >
                  <Text style={styles.chipText}>All</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.chip}
                  onPress={() => onConcentrationBucketsChange?.(
                    Object.fromEntries(AFFILIATE_BUCKETS.map((b) => [b.id, false]))
                  )}
                >
                  <Text style={styles.chipText}>None</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.chipRow}>
                {AFFILIATE_BUCKETS.map((b) => {
                  const on = concentrationBucketsOn[b.id] === true;
                  return (
                    <TouchableOpacity
                      key={b.id}
                      onPress={() => onConcentrationBucketsChange?.({
                        ...concentrationBucketsOn,
                        [b.id]: !on,
                      })}
                      style={[
                        styles.chip,
                        { flexDirection: 'row', alignItems: 'center', gap: 5, opacity: on ? 1 : 0.45 },
                        on && { borderColor: b.color, backgroundColor: `${b.color}26` },
                      ]}
                    >
                      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: b.color }} />
                      <Text style={[styles.chipText, on && { color: b.color, fontWeight: '700' }]}>{b.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </>
          )}
        </View>
      )}

      {!masterTourUi ? (
        <>
          <Text style={[styles.sectionLabel, { marginTop: 18 }]}>Pins & clusters</Text>
          <Text style={styles.sectionHint}>How individual venues are drawn on Standard view.</Text>
          <View style={styles.segmentRow}>
            {[
              { key: 'clusters', label: 'Clusters', icon: 'apps' },
              { key: 'pins', label: 'Pins', icon: 'location' },
              { key: 'hidden', label: 'Hide', icon: 'eye-off-outline' },
            ].map((opt) => {
              const active = displayMode === opt.key;
              return (
                <TouchableOpacity
                  key={opt.key}
                  onPress={() => onDisplayModeChange?.(opt.key)}
                  style={[styles.segmentOption, active && styles.segmentOptionActive]}
                >
                  <Ionicons
                    name={opt.icon}
                    size={12}
                    color={active ? '#fff' : (isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)')}
                  />
                  <Text style={[styles.segmentText, active && styles.segmentTextActive]}>{opt.label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </>
      ) : null}

      <View style={styles.quickActionsRow}>
        <TouchableOpacity
          style={[styles.quickAction, hasUserLocation && styles.quickActionActive]}
          onPress={onLocate}
        >
          <Ionicons name={hasUserLocation ? 'locate' : 'locate-outline'} size={15} color={isDarkMode ? '#fff' : '#333'} />
          <Text style={styles.quickActionText}>My location</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.quickAction} onPress={onRecenter}>
          <Ionicons name="scan-outline" size={15} color={isDarkMode ? '#fff' : '#333'} />
          <Text style={styles.quickActionText}>Recenter</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

function ToursPanel({
  isDarkMode,
  styles,
  savedTours = [],
  currentTourId,
  onCreateTour,
  onLoadTour,
}) {
  return (
    <ScrollView
      style={styles.panelScroll}
      contentContainerStyle={styles.panelScrollContent}
      nestedScrollEnabled
      showsVerticalScrollIndicator
      {...THIN_SCROLLBAR_PROPS}
    >
      <TouchableOpacity style={styles.createTourBtn} onPress={onCreateTour} activeOpacity={0.88}>
        <Ionicons name="add" size={18} color="#ffffff" />
        <Text style={styles.createTourBtnText}>Create new tour</Text>
      </TouchableOpacity>

      <Text style={[styles.sectionLabel, { marginTop: 16 }]}>Your tours</Text>
      {savedTours.length === 0 ? (
        <Text style={styles.emptyToursText}>
          No saved tours yet. Create one to start planning routes and days.
        </Text>
      ) : (
        <View style={styles.tourList}>
          {savedTours.map((tour) => {
            const active = tour.id === currentTourId;
            return (
              <TouchableOpacity
                key={tour.id}
                style={[styles.tourRow, active && styles.tourRowActive]}
                onPress={() => onLoadTour?.(tour.id)}
              >
                <Ionicons
                  name={active ? 'folder-open' : 'folder-outline'}
                  size={18}
                  color={active ? '#e67e22' : (isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)')}
                />
                <Text style={[styles.tourRowName, active && styles.tourRowNameActive]} numberOfLines={2}>
                  {tour.name}
                </Text>
                {active ? (
                  <View style={styles.tourActiveBadge}>
                    <Text style={styles.tourActiveBadgeText}>Active</Text>
                  </View>
                ) : null}
              </TouchableOpacity>
            );
          })}
        </View>
      )}
    </ScrollView>
  );
}

/**
 * Top-right Map / Tours menu for Tour Planner — replaces map control icons + hamburger.
 */
export default function RightMapToursRail({
  isDarkMode = true,
  isMobile = false,
  selectedTab = null,
  onSelectTab,
  rightOffset = 10,
  onWidthChange,
  tourPlannerOpen = false,
  // Map settings
  mapViewMode,
  onMapViewModeChange,
  displayMode,
  onDisplayModeChange,
  concentrationType,
  onConcentrationTypeChange,
  showConcentrationSettings,
  onToggleConcentrationSettings,
  concentrationBucketsOn,
  onConcentrationBucketsChange,
  basemapActive,
  onBasemapChange,
  onLocate,
  onRecenter,
  hasUserLocation,
  // Tours
  savedTours,
  currentTourId,
  onCreateTour,
  onLoadTour,
  masterTourUi = false,
}) {
  const styles = makeStyles(isDarkMode);
  const showMapPanel = selectedTab === 'map';
  const showToursPicker = selectedTab === 'tours' && !tourPlannerOpen;
  const panelOpen = showMapPanel || showToursPicker;
  const panelWidth = panelOpen ? RIGHT_RAIL_PANEL_WIDTH : 0;
  const totalWidth = tourPlannerOpen && !panelOpen ? RIGHT_RAIL_PANEL_WIDTH : panelWidth;

  React.useEffect(() => {
    onWidthChange?.(totalWidth);
  }, [totalWidth, onWidthChange]);

  const selectTab = (tab) => {
    onSelectTab?.(selectedTab === tab ? null : tab);
  };

  return (
    <View
      style={[
        styles.wrap,
        isMobile
          ? {
              left: MAP_SIDE_RAIL_MOBILE_WIDTH + MOBILE_LEFT_RAIL_GAP,
              right: MOBILE_RIGHT_EDGE_GAP,
              width: 'auto',
              maxWidth: 'none',
            }
          : { right: rightOffset },
        isMobile && styles.wrapMobile,
      ]}
      pointerEvents="box-none"
      {...(Platform.OS === 'web' ? { 'data-overlay-chrome': 'true' } : {})}
    >
      <View style={styles.tabRow} pointerEvents="auto">
        {[
          { id: 'map', label: 'Map', icon: 'map-outline' },
          { id: 'tours', label: 'Tours', icon: 'trail-sign-outline' },
        ].map((tab) => {
          const active = selectedTab === tab.id
            || (tab.id === 'tours' && tourPlannerOpen && selectedTab !== 'map');
          return (
            <TouchableOpacity
              key={tab.id}
              onPress={() => selectTab(tab.id)}
              style={[styles.tabBtn, active && styles.tabBtnActive]}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
            >
              <Ionicons
                name={tab.icon}
                size={14}
                color={active ? '#ffffff' : (isDarkMode ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.62)')}
              />
              <Text style={[styles.tabBtnText, active && styles.tabBtnTextActive]}>{tab.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {panelOpen ? (
        <View style={[styles.panel, isMobile && styles.panelMobile]} pointerEvents="auto">
          <View style={styles.panelHeader}>
            <Text style={styles.panelTitle}>
              {showMapPanel ? 'Map settings' : 'Tours'}
            </Text>
            <TouchableOpacity
              onPress={() => onSelectTab?.(null)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityLabel="Close panel"
            >
              <Text style={styles.panelClose}>✕</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.panelBody}>
          {showMapPanel ? (
            <MapSettingsPanel
              isDarkMode={isDarkMode}
              styles={styles}
              mapViewMode={mapViewMode}
              onMapViewModeChange={onMapViewModeChange}
              displayMode={displayMode}
              onDisplayModeChange={onDisplayModeChange}
              concentrationType={concentrationType}
              onConcentrationTypeChange={onConcentrationTypeChange}
              showConcentrationSettings={showConcentrationSettings}
              onToggleConcentrationSettings={onToggleConcentrationSettings}
              concentrationBucketsOn={concentrationBucketsOn}
              onConcentrationBucketsChange={onConcentrationBucketsChange}
              basemapActive={basemapActive}
              onBasemapChange={onBasemapChange}
              onLocate={onLocate}
              onRecenter={onRecenter}
              hasUserLocation={hasUserLocation}
              masterTourUi={masterTourUi}
            />
          ) : (
            <ToursPanel
              isDarkMode={isDarkMode}
              styles={styles}
              savedTours={savedTours}
              currentTourId={currentTourId}
              onCreateTour={onCreateTour}
              onLoadTour={onLoadTour}
            />
          )}
          </View>
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(isDarkMode) {
  const chromeBg = isDarkMode ? 'rgba(8, 10, 20, 0.78)' : 'rgba(255,255,255,0.92)';
  const panelBg = isDarkMode ? 'rgba(15,18,28,0.96)' : 'rgba(255,255,255,0.97)';
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';
  const textPri = isDarkMode ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.88)';
  const textSec = isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.52)';
  const textMuted = isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.42)';

  return StyleSheet.create({
    wrap: {
      position: 'absolute',
      top: 10,
      bottom: 10,
      zIndex: 15,
      alignItems: 'stretch',
      width: RIGHT_RAIL_PANEL_WIDTH,
      maxWidth: RIGHT_RAIL_PANEL_WIDTH,
      flexDirection: 'column',
      ...(Platform.OS === 'web' ? {
        display: 'flex',
      } : {}),
    },
    wrapMobile: {
      // Width comes from left/right insets so Map/Tours never cover the left rail.
      bottom: 8,
    },
    tabRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      alignSelf: 'flex-end',
      flexShrink: 0,
      backgroundColor: chromeBg,
      borderRadius: 10,
      borderWidth: 1,
      borderColor,
      padding: 4,
      ...(Platform.OS === 'web' ? {
        backdropFilter: 'blur(18px) saturate(160%)',
        WebkitBackdropFilter: 'blur(18px) saturate(160%)',
        boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.45)' : '0 4px 16px rgba(0,0,0,0.12)',
      } : {}),
    },
    tabBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderRadius: 8,
    },
    tabBtnActive: {
      backgroundColor: 'rgba(230,126,34,0.92)',
    },
    tabBtnText: {
      fontSize: 12.5,
      fontWeight: '700',
      color: textSec,
    },
    tabBtnTextActive: {
      color: '#ffffff',
    },
    panel: {
      marginTop: 8,
      width: '100%',
      flex: 1,
      minHeight: 0,
      backgroundColor: panelBg,
      borderRadius: 14,
      borderWidth: 1,
      borderColor,
      overflow: 'hidden',
      flexDirection: 'column',
      ...(Platform.OS === 'web' ? {
        display: 'flex',
        backdropFilter: 'blur(20px) saturate(160%)',
        WebkitBackdropFilter: 'blur(20px) saturate(160%)',
        boxShadow: isDarkMode ? '0 12px 40px rgba(0,0,0,0.55)' : '0 12px 40px rgba(0,0,0,0.14)',
      } : {}),
    },
    panelMobile: {
      width: '100%',
      maxWidth: '100%',
      alignSelf: 'stretch',
      flex: 1,
      minHeight: 0,
    },
    panelHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: borderColor,
      flexShrink: 0,
    },
    panelTitle: {
      fontSize: 15,
      fontWeight: '700',
      color: textPri,
    },
    panelClose: {
      fontSize: 16,
      color: textMuted,
      fontWeight: '600',
    },
    panelBody: {
      flex: 1,
      minHeight: 0,
      ...(Platform.OS === 'web' ? {
        display: 'flex',
        flexDirection: 'column',
      } : {}),
    },
    panelScroll: {
      flex: 1,
      minHeight: 0,
      ...(Platform.OS === 'web' ? {
        height: '100%',
      } : {}),
    },
    panelScrollContent: {
      padding: 14,
      paddingBottom: 24,
      flexGrow: 1,
    },
    sectionLabel: {
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: textMuted,
      marginBottom: 4,
    },
    sectionHint: {
      fontSize: 11.5,
      lineHeight: 16,
      color: textSec,
      marginBottom: 10,
    },
    typeCardList: {
      gap: 8,
    },
    typeCard: {
      borderRadius: 10,
      borderWidth: 1,
      borderColor,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
      overflow: 'hidden',
    },
    typeCardActive: {
      borderColor: '#e67e22',
      backgroundColor: isDarkMode ? 'rgba(230,126,34,0.18)' : 'rgba(230,126,34,0.12)',
    },
    previewSwatchRow: {
      flexDirection: 'row',
      height: 44,
    },
    previewSwatchSlice: {
      flex: 1,
    },
    previewSwatchSolid: {
      height: 44,
      position: 'relative',
      overflow: 'hidden',
    },
    previewSwatchGrid: {
      ...StyleSheet.absoluteFillObject,
      opacity: 0.15,
      borderWidth: 1,
      borderColor: 'rgba(255,255,255,0.3)',
    },
    typeCardBody: {
      padding: 10,
      gap: 4,
    },
    typeCardTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    typeCardTitle: {
      fontSize: 13,
      fontWeight: '700',
      color: textPri,
    },
    typeCardTitleActive: {
      color: isDarkMode ? '#ffffff' : '#1a1a1a',
    },
    typeCardDesc: {
      fontSize: 11,
      lineHeight: 15,
      color: textSec,
    },
    typeCardDescActive: {
      color: isDarkMode ? 'rgba(255,255,255,0.78)' : 'rgba(0,0,0,0.62)',
    },
    subPanel: {
      marginTop: 12,
      padding: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    },
    subPanelHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 6,
    },
    subPanelTitle: {
      fontSize: 12,
      fontWeight: '700',
      color: textPri,
    },
    subPanelToggle: {
      paddingHorizontal: 8,
      paddingVertical: 4,
    },
    subPanelToggleText: {
      fontSize: 11,
      fontWeight: '600',
      color: '#e67e22',
    },
    chipRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
      marginTop: 8,
    },
    chip: {
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 999,
      borderWidth: 1,
      borderColor,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
    },
    chipActive: {
      borderColor: '#e67e22',
      backgroundColor: 'rgba(230,126,34,0.2)',
    },
    chipText: {
      fontSize: 11,
      fontWeight: '600',
      color: textSec,
    },
    chipTextActive: {
      color: '#e67e22',
    },
    segmentRow: {
      flexDirection: 'row',
      gap: 3,
      padding: 3,
      borderRadius: 999,
      borderWidth: 1,
      borderColor,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
      alignSelf: 'flex-start',
    },
    segmentOption: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 999,
    },
    segmentOptionActive: {
      backgroundColor: '#e67e22',
    },
    segmentText: {
      fontSize: 11,
      fontWeight: '700',
      color: textSec,
    },
    segmentTextActive: {
      color: '#ffffff',
    },
    quickActionsRow: {
      flexDirection: 'row',
      gap: 8,
      marginTop: 14,
    },
    quickAction: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingVertical: 9,
      borderRadius: 8,
      borderWidth: 1,
      borderColor,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
    },
    quickActionActive: {
      borderColor: '#e67e22',
    },
    quickActionText: {
      fontSize: 11.5,
      fontWeight: '600',
      color: textPri,
    },
    createTourBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      backgroundColor: '#e67e22',
      borderRadius: 10,
      paddingVertical: 12,
    },
    createTourBtnText: {
      color: '#ffffff',
      fontSize: 14,
      fontWeight: '700',
    },
    currentTourBanner: {
      marginTop: 12,
      padding: 12,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: 'rgba(230,126,34,0.35)',
      backgroundColor: 'rgba(230,126,34,0.1)',
    },
    currentTourLabel: {
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      color: '#e67e22',
      marginBottom: 4,
    },
    currentTourName: {
      fontSize: 14,
      fontWeight: '700',
      color: textPri,
    },
    openPlannerLink: {
      marginTop: 8,
    },
    openPlannerLinkText: {
      fontSize: 12,
      fontWeight: '600',
      color: '#e67e22',
    },
    emptyToursText: {
      fontSize: 12.5,
      lineHeight: 18,
      color: textSec,
      marginTop: 4,
    },
    tourList: {
      marginTop: 8,
      gap: 6,
    },
    tourRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingVertical: 10,
      paddingHorizontal: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
    },
    tourRowActive: {
      borderColor: 'rgba(230,126,34,0.45)',
      backgroundColor: 'rgba(230,126,34,0.1)',
    },
    tourRowName: {
      flex: 1,
      fontSize: 13,
      fontWeight: '600',
      color: textPri,
    },
    tourRowNameActive: {
      color: '#e67e22',
    },
    tourActiveBadge: {
      paddingHorizontal: 7,
      paddingVertical: 3,
      borderRadius: 999,
      backgroundColor: 'rgba(230,126,34,0.2)',
    },
    tourActiveBadgeText: {
      fontSize: 9,
      fontWeight: '700',
      color: '#e67e22',
      textTransform: 'uppercase',
    },
  });
}
